import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Sparkles } from 'lucide-react';

/**
 * Agent narration block primitives — used in Stages 2-8 to render the
 * structured Summary / Details / Transition text + Tool Output cards
 * + end-of-turn rollups.
 */

/* ───────── Section heading ───────── */

export const SectionHeading: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <h3 className={`text-cargo-text font-semibold text-[16px] leading-relaxed ${className}`}>
    {children}
  </h3>
);

/* ───────── Key-value list ───────── */

export interface KVRow {
  label: string;
  value: React.ReactNode;
}

export const KeyValueList: React.FC<{ rows: KVRow[]; className?: string }> = ({
  rows, className = '',
}) => (
  <ul className={`space-y-2 ml-1 ${className}`}>
    {rows.map((r, i) => (
      <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed">
        <span className="mt-2 w-1 h-1 rounded-full bg-cargo-muted flex-shrink-0" />
        <span>
          <span className="text-cargo-text font-semibold">{r.label}</span>
          <span className="text-cargo-muted">: </span>
          <span className="text-cargo-muted">{r.value}</span>
        </span>
      </li>
    ))}
  </ul>
);

/* ───────── Transition text (italic-ish one-liner between blocks) ───────── */

export const TransitionText: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <p className={`text-cargo-muted text-[13px] leading-relaxed ${className}`}>
    {children}
  </p>
);

/* ───────── Tool Output card (collapsible) ───────── */

interface ToolOutputCardProps {
  toolName: string;
  rawContent?: string;
  defaultOpen?: boolean;
  status?: React.ReactNode;     // optional status badge to render inline
  children?: React.ReactNode;   // optional rich content to render when expanded
}

export const ToolOutputCard: React.FC<ToolOutputCardProps> = ({
  toolName, rawContent, defaultOpen = false, status, children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-cargo-border bg-cargo-surface overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-cargo-text hover:bg-cargo-panel transition-colors"
      >
        <span className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-cargo-accent" />
          <span className="text-[13px]">
            <span className="text-cargo-muted">Tool Output</span>
            <span className="text-cargo-muted mx-1.5">•</span>
            <span className="text-cargo-text font-mono">{toolName}</span>
          </span>
          {status && <span className="ml-2">{status}</span>}
        </span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-cargo-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 text-cargo-muted" />
        )}
      </button>
      {open && (
        <div className="border-t border-cargo-border p-4 space-y-3">
          {children}
          {rawContent && (
            <pre className="text-[11px] text-cargo-muted font-mono bg-cargo-surface rounded p-3 overflow-x-auto max-h-80 leading-relaxed">
              {tryPretty(rawContent)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

function tryPretty(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/* ───────── Quality Summary block ───────── */

interface QualitySummaryBlockProps {
  overallScore: number;
  recordCount: number;
  narrative: string;
  distribution: { excellent: number; moderate: number; poor: number };
  topPriority: string;
}

export const QualitySummaryBlock: React.FC<QualitySummaryBlockProps> = ({
  overallScore, recordCount, narrative, distribution, topPriority,
}) => {
  const dotForScore = (s: number) =>
    s >= 95
      ? 'bg-cargo-success shadow-[0_0_8px_rgba(52,211,153,0.6)]'
      : s >= 90
        ? 'bg-cargo-warning shadow-[0_0_8px_rgba(251,191,36,0.6)]'
        : 'bg-cargo-error shadow-[0_0_8px_rgba(239,68,68,0.6)]';
  return (
    <div className="space-y-3">
      <SectionHeading>Quality Summary</SectionHeading>
      <p className="text-cargo-muted text-[13.5px] leading-relaxed">
        Your dataset has{' '}
        <span className="text-cargo-text font-bold">{overallScore.toFixed(1)}%</span>{' '}
        <span className={`inline-block w-2.5 h-2.5 rounded-full align-middle mx-0.5 ${dotForScore(overallScore)}`} />{' '}
        overall quality with <span className="text-cargo-text font-semibold">{recordCount.toLocaleString()}</span> records analyzed.
        {' '}
        {narrative.replace(/^Your dataset has [\d.]+% overall quality with [\d,]+ records analyzed\.\s*/i, '').replace(/^The data /, '')}
      </p>

      <ul className="text-[13px] leading-relaxed flex flex-wrap gap-x-4 gap-y-1 text-cargo-muted">
        <li>• Total Columns Analyzed: <span className="text-cargo-text font-semibold">{distribution.excellent + distribution.moderate + distribution.poor}</span></li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-cargo-success shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
          Excellent (≥95%): <span className="text-cargo-text font-semibold">{distribution.excellent} columns</span>
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-cargo-warning shadow-[0_0_8px_rgba(251,191,36,0.6)]" />
          Moderate (90-95%): <span className="text-cargo-text font-semibold">{distribution.moderate} columns</span>
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-cargo-error shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
          Poor (&lt;90%): <span className="text-cargo-text font-semibold">{distribution.poor} columns</span>
        </li>
      </ul>

      {topPriority && (
        <p className="text-[13px] leading-relaxed text-cargo-muted">
          <span className="text-cargo-text font-semibold">Top Priority</span>: {topPriority}
        </p>
      )}
    </div>
  );
};
